/**
 * AdminOnboardingQR
 *
 * Generates and displays the physical-panel QR code that a new client scans
 * during mobile app onboarding to become the account admin.
 *
 * - One active code per SITE at a time (an account can have several sites/panels).
 * - No expiry (code stays on physical panel).
 * - Shows who redeemed it and when, if already used.
 * - Operator can revoke and regenerate.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { QrCode, RefreshCw, Download, Copy, Shield, User, CheckCircle2, XCircle, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/services/supabase';

interface Invitation {
  id: string;
  serial_code: string;
  display_name: string | null;
  created_at: string;
  used_at: string | null;
  revoked_at: string | null;
  used_by_profile: { full_name: string; email: string } | null;
}

interface Props {
  accountId: string;
  siteId: string;
  siteName: string;
}

export function AdminOnboardingQR({ accountId, siteId, siteName }: Props) {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [existingAdmin, setExistingAdmin] = useState<{ full_name: string; email: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<SVGSVGElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  const deepLink = invitation
    ? `asisecurity://join?code=${invitation.serial_code}`
    : '';

  const load = useCallback(async () => {
    setLoading(true);
    const [invRes, memberRes] = await Promise.all([
      supabase
        .from('account_invitations')
        .select(`id, serial_code, display_name, created_at, used_at, revoked_at, used_by_profile:profiles!used_by(full_name, email)`)
        .eq('site_id', siteId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      // Detect legacy admin/owner so QR shows correct "already configured" state.
      // Skip entirely when the site has no account yet (accountId='') — there's
      // no owner to find, and account_id is a UUID column that would reject ''.
      accountId
        ? supabase
            .from('account_members')
            .select('profiles!user_id(full_name, email)')
            .eq('account_id', accountId)
            .in('role', ['owner', 'admin'])
            .eq('is_active', true)
            .limit(1)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    setInvitation(invRes.data as Invitation | null);
    const profile = (memberRes.data as any)?.profiles;
    setExistingAdmin(profile ? { full_name: profile.full_name, email: profile.email } : null);
    setLoading(false);
  }, [accountId, siteId]);

  useEffect(() => { load(); }, [load]);

  async function generate() {
    setGenerating(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ site_id: siteId, display_name: siteName }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  async function revoke() {
    if (!invitation) return;
    if (!confirm('¿Revocar este código? Se generará uno nuevo.')) return;
    setRevoking(true);
    const { data: { user } } = await supabase.auth.getUser();
    await supabase
      .from('account_invitations')
      .update({ revoked_at: new Date().toISOString(), revoked_by: user?.id })
      .eq('id', invitation.id);
    await generate();
    setRevoking(false);
  }

  function copyCode() {
    if (!invitation) return;
    navigator.clipboard.writeText(invitation.serial_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadQR() {
    if (!qrRef.current || !invitation) return;
    const svg = qrRef.current;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `QR-Admin-${invitation.serial_code}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadBrandedImage(format: 'png' | 'jpg') {
    if (!qrCanvasRef.current || !invitation) return;
    const qrCanvas = qrCanvasRef.current;
    const SCALE = 2;
    const W = 400, H = 540;
    const canvas = document.createElement('canvas');
    canvas.width = W * SCALE;
    canvas.height = H * SCALE;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(SCALE, SCALE);

    // Background
    ctx.fillStyle = '#010816';
    ctx.beginPath();
    const r = 16;
    ctx.moveTo(r, 0); ctx.lineTo(W - r, 0);
    ctx.quadraticCurveTo(W, 0, W, r);
    ctx.lineTo(W, H - r); ctx.quadraticCurveTo(W, H, W - r, H);
    ctx.lineTo(r, H); ctx.quadraticCurveTo(0, H, 0, H - r);
    ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fill();

    const drawRest = () => {
      // White QR area
      const qrSize = 200, qrX = (W - qrSize) / 2, qrY = 120;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(qrX - 12, qrY - 12, qrSize + 24, qrSize + 24, 10);
      ctx.fill();
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

      // Serial code
      ctx.fillStyle = '#F4B916';
      ctx.font = `bold ${18 * SCALE / SCALE}px "Courier New", monospace`;
      ctx.textAlign = 'center';
      ctx.letterSpacing = '0.2em';
      ctx.fillText(invitation!.serial_code, W / 2, qrY + qrSize + 40);

      // Corporación A.S.I.
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `600 14px sans-serif`;
      ctx.letterSpacing = 'normal';
      ctx.fillText('Corporación A.S.I.', W / 2, qrY + qrSize + 64);

      // PBX
      ctx.fillStyle = '#A1A8B8';
      ctx.font = `13px sans-serif`;
      ctx.fillText('PBX: 2426 2426', W / 2, qrY + qrSize + 84);

      // Footer divider
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(32, H - 48); ctx.lineTo(W - 32, H - 48); ctx.stroke();
      ctx.fillStyle = '#4A5568';
      ctx.font = '11px sans-serif';
      ctx.fillText('Escanea con la app ASI Security', W / 2, H - 28);

      const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
      const a = document.createElement('a');
      a.href = canvas.toDataURL(mime, 0.95);
      a.download = `QR-Admin-${invitation!.serial_code}.${format}`;
      a.click();
    };

    // Draw logo at top
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.src = '/logo_dark.png';
    logo.onload = () => {
      const logoH = 40, logoW = Math.round(logo.naturalWidth * (logoH / logo.naturalHeight));
      ctx.drawImage(logo, (W - logoW) / 2, 28, logoW, logoH);
      ctx.fillStyle = '#A1A8B8';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Corporación A.S.I.', W / 2, 84);
      drawRest();
    };
    logo.onerror = () => {
      // Fallback: text only
      ctx.fillStyle = '#F4B916';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Corporación A.S.I.', W / 2, 60);
      drawRest();
    };
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // The sticker is printed on the physical panel and must stay reusable across
  // clients — a cancelled client's site gets released and the SAME code works
  // again for the next buyer. So "is this code currently claimed" depends on
  // whether the site/account has a LIVE owner right now (existingAdmin), not
  // on whether invitation.used_at was ever set in the past.
  const isCurrentlyOwned = existingAdmin != null;
  const isActive = invitation && !invitation.revoked_at && !isCurrentlyOwned;
  const hasHistory = invitation?.used_at != null;
  const redeemedBy = invitation?.used_by_profile ?? existingAdmin;

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <QrCode className="w-4 h-4 text-primary" />
              Código QR de administrador
            </CardTitle>
            <CardDescription className="mt-1">
              Este QR se pega en el panel físico. El cliente lo escanea para registrarse
              como administrador de la cuenta. Si el cliente ya tiene una cuenta de solo app,
              el sistema detecta esto automáticamente y vincula el panel a su cuenta existente
              en vez de crear una cuenta nueva.
            </CardDescription>
          </div>
          {/* Generate button — only when no invitation exists AND no owner already claimed the account */}
          {!invitation && !existingAdmin && (
            <Button size="sm" onClick={generate} disabled={generating}>
              {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
              <span className="ml-1">Generar código</span>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Account already has an owner (e.g. client self-registered as app-only
            before this panel was sold) — a QR would be pointless here: it can
            only grant ownership, and this account already has one. */}
        {!invitation && existingAdmin && (
          <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-sm">
            <User className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-amber-700">
                Esta cuenta ya tiene un titular: {existingAdmin.full_name} ({existingAdmin.email})
              </p>
              <p className="text-muted-foreground mt-1">
                El cliente ya se registró por cuenta propia (solo app), así que no necesita escanear
                ningún QR para reclamar esta cuenta. Para agregarle alarma/cámaras, ve a la pestaña
                <strong> Servicios → Modo de registro</strong> y usa "Promover a cuenta gestionada".
              </p>
            </div>
          </div>
        )}

        {!invitation && !existingAdmin && (
          <div className="text-center py-6 text-muted-foreground text-sm">
            No hay código generado para esta cuenta.
          </div>
        )}

        {invitation && (
          <div className="space-y-4">
            {/* Status badge */}
            <div className="flex items-center gap-2">
              {invitation.revoked_at ? (
                <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Revocado
                </Badge>
              ) : isCurrentlyOwned ? (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Canjeado
                </Badge>
              ) : (
                <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Activo — pendiente de canje
                </Badge>
              )}
            </div>

            {/* QR + code */}
            {isActive && (
              <div className="flex flex-col sm:flex-row gap-6 items-center">
                <div className="flex flex-col items-center gap-2 bg-[#010816] rounded-xl p-5 border border-white/10">
                  <img src="/logo_dark.png" alt="ASI" className="h-8 object-contain" />
                  <div className="bg-white rounded-lg p-2 mt-2">
                    <QRCodeSVG
                      ref={qrRef as any}
                      value={deepLink}
                      size={160}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  {/* Hidden canvas for PNG/JPG export */}
                  <div className="hidden">
                    <QRCodeCanvas
                      ref={qrCanvasRef as any}
                      value={deepLink}
                      size={200}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <p className="font-mono text-sm font-bold tracking-widest text-[#F4B916] mt-1">
                    {invitation.serial_code}
                  </p>
                  <p className="text-white/70 text-[11px] font-medium">Corporación A.S.I.</p>
                  <p className="text-white/40 text-[10px]">PBX: 2426 2426</p>
                </div>

                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Código serial</p>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-lg font-bold tracking-widest bg-muted px-3 py-1 rounded">
                        {invitation.serial_code}
                      </code>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copyCode} title="Copiar código">
                        {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      {isActive && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={revoke} disabled={revoking} title="Regenerar código">
                          {revoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Deep link (para compartir)</p>
                    <code className="text-xs text-muted-foreground break-all">{deepLink}</code>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={downloadQR}>
                      <Download className="w-3 h-3 mr-1" />
                      SVG
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadBrandedImage('png')}>
                      <ImageIcon className="w-3 h-3 mr-1" />
                      PNG
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadBrandedImage('jpg')}>
                      <ImageIcon className="w-3 h-3 mr-1" />
                      JPG
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Generado el {new Date(invitation.created_at).toLocaleDateString('es-GT', {
                      day: '2-digit', month: 'long', year: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Redeemed info — from invitation or legacy admin member */}
            {hasHistory && redeemedBy && (
              <div className={`flex items-start gap-3 rounded-lg p-3 border ${isCurrentlyOwned ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/40 border-border'}`}>
                <User className={`w-4 h-4 mt-0.5 ${isCurrentlyOwned ? 'text-green-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-sm font-medium ${isCurrentlyOwned ? 'text-green-700' : 'text-foreground'}`}>
                    {isCurrentlyOwned ? 'Administrador: ' : 'Último titular (ya liberado): '}{redeemedBy.full_name}
                  </p>
                  <p className={`text-xs ${isCurrentlyOwned ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {redeemedBy.email}
                    {invitation?.used_at ? ` — ${new Date(invitation.used_at).toLocaleString('es-GT')}` : ''}
                  </p>
                  {isCurrentlyOwned ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Para reasignar, degrada al usuario actual desde la pestaña Miembros
                      y luego vuelve a usar este mismo código.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      El sitio está libre — el código de arriba (el mismo del sticker impreso)
                      ya está activo de nuevo para que el siguiente cliente lo escanee.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Revoked by an operator — this is the only case where the
                physical sticker's code truly stops working and a new one
                must be printed. */}
            {invitation.revoked_at && (
              <Button size="sm" onClick={generate} disabled={generating}>
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                <span className="ml-1">Generar nuevo código</span>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
